/* eslint-disable no-param-reassign */
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Button, Popconfirm, Space, Tooltip } from 'antd'
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  FallOutlined,
  LineChartOutlined,
  OrderedListOutlined
} from '@ant-design/icons'
import { ColDef, ColGroupDef, ICellRendererParams } from 'ag-grid-enterprise'
import { VariableType } from '../../../components/Table/types'
import { Loan } from '../types/Loan'
import { LoanSummaryLoan } from '../types/LoanSummary'
import { Purpose } from '../../../types/purpose/Purpose'
import { formatValueByType } from '../../../utils/helpers'
import { parseDate } from '../../../config/dayjsConfig'
import { loanRepaymentMethodsSelector } from '../../../redux/entities/loanRepaymentMethods/selectors'
import { normalizedConfigSelector } from '../../../redux/config/selectors'
import { liabilityMappingSelector } from '../../../redux/config/liabilityMapping/selectors'
import { investmentsPageSelector } from '../../../redux/pages/investments/selectors'
import { useFiscalYearToDateLabel } from '../../investment/hooks/hooks'
import { useCtxDimensions } from '../../dimension/queries/useCtxDimensions'
import DimensionName from '../../dimension/components/DimensionName'
import {
  AccountSelectCellEditor,
  DimensionTreeSelectCellEditor,
  NumericCellEditor,
  PurposesCellEditor,
  SelectCellEditor
} from '../../../shared/components/custom/Grid/Editor'
import { useGridFormatters } from '../../../shared/hooks/useGridFormatters'

interface UseLoanColumnsOptions {
  loans: Loan[]
  isExisting: boolean
  isLocked?: boolean
  balanceSheetRowId: number
  onStartEdit: (loan: Loan) => void
  onSaveEdit: () => void
  onCancelEdit: () => void
  onDelete: (loan: Loan) => void
  onEditClick: (loan: Loan) => void
  toggleRepaymentSchedule: (e: React.MouseEvent<HTMLElement, MouseEvent>, loan: Loan) => void
  toggleInterestRate: (e: React.MouseEvent<HTMLElement, MouseEvent>, loan: Loan) => void
}

export const useLoanColumns = ({
  loans,
  isExisting,
  isLocked = false,
  balanceSheetRowId,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onEditClick,
  toggleRepaymentSchedule,
  toggleInterestRate
}: UseLoanColumnsOptions) => {
  const { t } = useTranslation()
  const { percentage } = useGridFormatters()
  const loanRepaymentMethods = useSelector(loanRepaymentMethodsSelector)
  const {
    financialStatement: { financialExpenseRowId }
  } = useSelector(normalizedConfigSelector)
  const liabilityMapping = useSelector(liabilityMappingSelector)
  const { date } = useSelector(investmentsPageSelector)
  const fiscalYearToDateLabel = useFiscalYearToDateLabel()
  const { data: dimensions = [] } = useCtxDimensions()

  const dateLabel = parseDate(date)?.format('MM/YYYY') ?? ''

  const repaymentMethodOptions = useMemo(
    () =>
      loanRepaymentMethods.map(m => ({
        value: m.id,
        label: t(`loanRepaymentMethod:${m.id}`)
      })),
    [loanRepaymentMethods, t]
  )

  const columnDefs = useMemo<(ColDef<LoanSummaryLoan> | ColGroupDef<LoanSummaryLoan>)[]>(() => {
    const editable = isExisting && !isLocked
    const cols: (ColDef<LoanSummaryLoan> | ColGroupDef<LoanSummaryLoan>)[] = [
      {
        headerName: t('loansTable:purposes'),
        colId: 'purposes',
        editable,
        pinned: 'left',
        width: 100,
        filter: 'agTextColumnFilter',
        cellEditor: PurposesCellEditor,
        valueGetter: params => params.data?.purposes?.map((p: Purpose) => p.name) ?? [],
        valueSetter: params => {
          params.data.purposes = (params.newValue as string[])?.map((name: string) => ({ name })) as Purpose[]
          return true
        },
        valueFormatter: params => (params.value as string[])?.join(', ') ?? ''
      },
      {
        headerName: t('loansTable:value'),
        field: 'value',
        editable,
        pinned: 'left',
        width: 100,
        filter: 'agNumberColumnFilter',
        cellEditor: NumericCellEditor,
        cellClass: 'ag-right-aligned-cell',
        valueFormatter: params => formatValueByType(params.value, VariableType.absolute)
      },
      {
        headerName: t('loansTable:nextInstalmentDate'),
        field: 'nextInstalmentDate',
        type: 'datePicker',
        editable,
        width: 120,
        filter: 'agDateColumnFilter'
      },
      {
        headerName: t('loansTable:financialExpensesAccountCode'),
        field: 'financialExpensesAccountCode',
        editable,
        width: 100,
        cellEditor: AccountSelectCellEditor,
        cellEditorParams: {
          balanceSheetRowIds: financialExpenseRowId
        },
        cellClass: 'ag-right-aligned-cell',
        filter: 'agSetColumnFilter'
      },
      {
        headerName: t('loansTable:longTermAccountCode'),
        field: 'longTermAccountCode',
        editable,
        width: 100,
        cellEditor: AccountSelectCellEditor,
        cellEditorParams: {
          balanceSheetRowIds: [balanceSheetRowId]
        },
        cellClass: 'ag-right-aligned-cell',
        filter: 'agSetColumnFilter'
      },
      {
        headerName: t('loansTable:shortTermAccountCode'),
        field: 'shortTermAccountCode',
        editable,
        width: 100,
        cellEditor: AccountSelectCellEditor,
        cellEditorParams: {
          balanceSheetRowIds: liabilityMapping?.[balanceSheetRowId] ? [liabilityMapping[balanceSheetRowId]] : []
        },
        cellClass: 'ag-right-aligned-cell',
        filter: 'agSetColumnFilter'
      },
      {
        groupId: 'term',
        headerName: t('loansTable:term'),
        headerTooltip: t('loansTable:term/info'),
        children: [
          {
            headerName: t('loansTable:termYears', { count: 2 }),
            field: 'termYears',
            editable,
            width: 80,
            filter: 'agNumberColumnFilter',
            cellEditor: NumericCellEditor,
            cellClass: 'ag-right-aligned-cell'
          },
          {
            headerName: t('loansTable:termMonths', { count: 2 }),
            field: 'termMonths',
            editable,
            width: 80,
            filter: 'agNumberColumnFilter',
            cellEditor: NumericCellEditor,
            cellClass: 'ag-right-aligned-cell'
          }
        ] as ColDef<LoanSummaryLoan>[]
      },
      {
        groupId: 'repaymentProgram',
        headerName: t('loansTable:repaymentProgram'),
        headerTooltip: t('loansTable:repaymentProgram/info'),
        children: [
          {
            headerName: t('loansTable:repaymentProgramYears', { count: 2 }),
            field: 'repaymentProgramYears',
            editable,
            width: 80,
            filter: 'agNumberColumnFilter',
            cellEditor: NumericCellEditor,
            cellClass: 'ag-right-aligned-cell'
          },
          {
            headerName: t('loansTable:repaymentProgramMonths', { count: 2 }),
            field: 'repaymentProgramMonths',
            editable,
            width: 80,
            filter: 'agNumberColumnFilter',
            cellEditor: NumericCellEditor,
            cellClass: 'ag-right-aligned-cell'
          }
        ] as ColDef<LoanSummaryLoan>[]
      },
      {
        headerName: t('loansTable:yearlyInstalments'),
        field: 'yearlyInstalments',
        editable,
        width: 90,
        filter: 'agNumberColumnFilter',
        cellEditor: NumericCellEditor,
        cellClass: 'ag-right-aligned-cell'
      },
      {
        headerName: t('loansTable:instalmentFreePeriod'),
        headerTooltip: t('loansTable:instalmentFreePeriod/info'),
        field: 'instalmentFreePeriod',
        editable,
        width: 90,
        filter: 'agNumberColumnFilter',
        cellEditor: NumericCellEditor,
        cellClass: 'ag-right-aligned-cell'
      },
      {
        headerName: t('loansTable:loanRepaymentMethodId'),
        field: 'loanRepaymentMethodId',
        editable,
        width: 110,
        cellEditor: SelectCellEditor,
        cellEditorParams: {
          options: repaymentMethodOptions
        },
        cellClass: 'ag-right-aligned-cell',
        filter: 'agSetColumnFilter',
        filterParams: {
          valueFormatter: (params: { value: number }) => (params.value ? t(`loanRepaymentMethod:${params.value}`) : '')
        },
        valueFormatter: params => (params.value ? t(`loanRepaymentMethod:${params.value}`) : '')
      },
      {
        headerName: t('loansTable:interestRate'),
        field: 'interestRate',
        type: 'percentage',
        editable,
        width: 100,
        filter: 'agNumberColumnFilter',
        filterValueGetter: params => (params.data?.interestRate != null ? params.data.interestRate * 100 : null),
        cellEditorParams: { precision: 6, decimals: 3 },
        cellClass: 'ag-right-aligned-cell',
        valueFormatter: percentage(3)
      },
      {
        headerName: t('loansTable:dimensionId'),
        field: 'dimensionId',
        editable,
        width: 130,
        cellEditor: DimensionTreeSelectCellEditor,
        cellClass: 'ag-right-aligned-cell',
        filter: 'agSetColumnFilter',
        cellRenderer: (params: ICellRendererParams<LoanSummaryLoan>) => {
          if (!params.value) return null
          return <DimensionName dimensionId={params.value} />
        },
        filterParams: {
          values: (params: { success: (values: string[]) => void }) => {
            params.success([...new Set(loans.map(l => l.dimensionId).filter(Boolean) as string[])])
          },
          cellRenderer: (params: ICellRendererParams) => {
            if (!params.value) return '(empty)'
            const dim = dimensions?.find(d => d.dimensionId === params.value)
            return dim?.name || params.value
          }
        }
      },
      {
        groupId: 'interest',
        headerName: t('loansTable:summary/interestValue'),
        children: [
          {
            headerName: dateLabel,
            field: 'summary.interestValue',
            width: 80,
            cellClass: 'ag-right-aligned-cell',
            filter: 'agNumberColumnFilter',
            valueFormatter: params => formatValueByType(params.value, VariableType.absolute)
          },
          {
            headerName: fiscalYearToDateLabel,
            field: 'summary.fiscalYearInterestValue',
            width: 80,
            cellClass: 'ag-right-aligned-cell',
            filter: 'agNumberColumnFilter',
            valueFormatter: params => formatValueByType(params.value, VariableType.absolute)
          }
        ] as ColDef<LoanSummaryLoan>[]
      },
      {
        groupId: 'instalment',
        headerName: t('loansTable:summary/instalmentValue'),
        children: [
          {
            headerName: dateLabel,
            field: 'summary.instalmentValue',
            width: 80,
            cellClass: 'ag-right-aligned-cell',
            filter: 'agNumberColumnFilter',
            valueFormatter: params => formatValueByType(params.value, VariableType.absolute)
          },
          {
            headerName: fiscalYearToDateLabel,
            field: 'summary.fiscalYearInstalmentValue',
            width: 80,
            cellClass: 'ag-right-aligned-cell',
            filter: 'agNumberColumnFilter',
            valueFormatter: params => formatValueByType(params.value, VariableType.absolute)
          }
        ] as ColDef<LoanSummaryLoan>[]
      },
      {
        headerName: t('loansTable:summary/budgetBalance'),
        field: 'summary.budgetBalance',
        width: 90,
        pinned: 'right',
        cellClass: 'ag-right-aligned-cell',
        filter: 'agNumberColumnFilter',
        valueFormatter: params => formatValueByType(params.value, VariableType.absolute)
      },
      {
        headerName: t('loansTable:repaymentSchedule'),
        colId: 'repaymentSchedule',
        width: 60,
        pinned: 'right',
        sortable: false,
        filter: false,
        cellRenderer: (params: ICellRendererParams<LoanSummaryLoan>) => (
          <Tooltip placement="top" title={t('loansTable:repaymentSchedule')}>
            <Button
              block
              size="small"
              style={{ marginRight: '8px' }}
              icon={<OrderedListOutlined />}
              onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) =>
                params.data && toggleRepaymentSchedule(e, params.data)
              }
            />
          </Tooltip>
        )
      },
      {
        headerName: t('loansTable:interestRates'),
        colId: 'interestRates',
        width: 60,
        pinned: 'right',
        sortable: false,
        filter: false,
        cellRenderer: (params: ICellRendererParams<LoanSummaryLoan>) => (
          <Tooltip placement="top" title={t('loansTable:changing-interest')}>
            <Button
              block
              size="small"
              style={{ marginRight: '8px' }}
              icon={<LineChartOutlined />}
              onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) =>
                params.data && toggleInterestRate(e, params.data)
              }
            />
          </Tooltip>
        )
      }
    ]

    if (isExisting) {
      cols.push({
        headerName: t('global:actions'),
        colId: 'actions',
        pinned: 'right',
        width: 80,
        sortable: false,
        filter: false,
        editable: false,
        cellRenderer: (params: ICellRendererParams<LoanSummaryLoan>) => {
          if (!params.data) return null
          const editingRowId = params.context?.editingRowIdRef?.current ?? null
          const isEditingThisRow = editingRowId === params.data.id

          if (isEditingThisRow) {
            return (
              <Space>
                <Button size="small" type="link" icon={<CheckOutlined />} onClick={onSaveEdit} />
                <Button size="small" type="link" icon={<CloseOutlined />} onClick={onCancelEdit} />
              </Space>
            )
          }

          return (
            <Space>
              <Button
                size="small"
                type="link"
                icon={<EditOutlined />}
                onClick={() => onStartEdit(params.data!)}
                disabled={editingRowId !== null || isLocked}
              />
              <Popconfirm
                placement="topRight"
                title={t('global:delete-confirm')}
                onConfirm={() => params.data && onDelete(params.data)}
              >
                <Button
                  danger
                  size="small"
                  type="link"
                  icon={<DeleteOutlined />}
                  disabled={editingRowId !== null || isLocked}
                />
              </Popconfirm>
            </Space>
          )
        }
      })
    } else {
      cols.push({
        headerName: t('global:actions'),
        colId: 'actions',
        pinned: 'right',
        width: 80,
        sortable: false,
        filter: false,
        cellRenderer: (params: ICellRendererParams<LoanSummaryLoan>) => {
          if (!params.data) return null
          return (
            <Space>
              <Button
                size="small"
                type="link"
                icon={<EditOutlined />}
                onClick={() => params.data && onEditClick(params.data)}
                disabled={isLocked}
              />
              <Popconfirm
                placement="topRight"
                title={t('global:delete-confirm')}
                onConfirm={() => params.data && onDelete(params.data)}
              >
                <Button danger size="small" type="link" icon={<DeleteOutlined />} disabled={isLocked} />
              </Popconfirm>
            </Space>
          )
        }
      })
    }

    return isExisting ? cols : cols.filter(c => (c as ColDef).colId !== 'purposes')
  }, [
    t,
    isLocked,
    isExisting,
    balanceSheetRowId,
    dateLabel,
    fiscalYearToDateLabel,
    financialExpenseRowId,
    liabilityMapping,
    repaymentMethodOptions,
    loans,
    dimensions,
    onStartEdit,
    onSaveEdit,
    onCancelEdit,
    onDelete,
    onEditClick,
    toggleRepaymentSchedule,
    toggleInterestRate
  ])

  return { columnDefs }
}
